import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { IconCompass, IconHeart, IconMail } from '../components/Icons';
import { Link } from 'react-router-dom';
import { toApiError, type ApiError } from '../api/client';
import { authApi, billingApi, meApi, propertyApi } from '../api/endpoints';
import type { Enquiry, Favourite, ListingPlan, PagedResult } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Field, Pagination } from '../components/Common';
import { PropertyCard } from '../components/PropertyCard';
import { EmptyState, ErrorState, InlineError, InlineSuccess, ListingGridSkeleton, TableSkeleton } from '../components/States';
import { categoryLabel, dateTime, humanise, money, plainDate } from '../utils/format';

export function FavouritesPage() {
  const [data, setData] = useState<PagedResult<Favourite> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setData(await meApi.favourites(page));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (propertyId: string) => {
    try {
      await propertyApi.toggleFavourite(propertyId);
      await load();
    } catch {
      /* keep the list as it is */
    }
  };

  return (
    <div className="container">
      <h1 className="h3 mb-3">Saved properties</h1>

      {loading && <ListingGridSkeleton count={3} />}
      {!loading && error && <ErrorState error={error} onRetry={load} />}

      {!loading && !error && data && data.items.length === 0 && (
        <EmptyState
          icon={<IconHeart />}
          title="Nothing saved yet"
          description="Tap the heart on any listing to keep it here for later."
          action={
            <Link to="/pg" className="btn btn-primary">
              Browse PG & hostels
            </Link>
          }
        />
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <>
          <div className="row g-3">
            {data.items.map((favourite) => (
              <div className="col-12 col-sm-6 col-lg-4" key={favourite.propertyId}>
                <PropertyCard
                  property={favourite.property}
                  isSaved
                  onToggleSave={remove}
                  showDistance={false}
                />
              </div>
            ))}
          </div>

          <Pagination result={data} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

export function MyEnquiriesPage() {
  const [data, setData] = useState<PagedResult<Enquiry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setData(await meApi.enquiries(page));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="container">
      <h1 className="h3 mb-3">My enquiries</h1>

      {loading && <TableSkeleton rows={4} columns={4} />}
      {!loading && error && <ErrorState error={error} onRetry={load} />}

      {!loading && !error && data && data.items.length === 0 && (
        <EmptyState
          icon={<IconMail />}
          title="No enquiries yet"
          description="When you contact a property through RoomKart, it will appear here."
        />
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <>
          <div className="sf-table-wrap">
            <table className="sf-table">
              <thead>
                <tr>
                  <th scope="col">Property</th>
                  <th scope="col">Sent</th>
                  <th scope="col">Dates</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((enquiry) => (
                  <tr key={enquiry.id}>
                    <td>
                      <Link to={`/listing/${enquiry.propertyId}`}>{enquiry.propertyTitle}</Link>
                      {enquiry.roomTypeName && <div className="sf-muted small">{enquiry.roomTypeName}</div>}
                    </td>
                    <td className="sf-muted small">{dateTime(enquiry.createdAtUtc)}</td>
                    <td className="sf-muted small">
                      {enquiry.checkInDate
                        ? `${plainDate(enquiry.checkInDate)} – ${plainDate(enquiry.checkOutDate)}`
                        : enquiry.moveInDate
                          ? `Move in ${plainDate(enquiry.moveInDate)}`
                          : '—'}
                    </td>
                    <td>
                      <span className={`sf-pill sf-pill--${enquiry.status === 'Contacted' ? 'ok' : 'info'}`}>
                        {humanise(enquiry.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination result={data} onPageChange={setPage} />
        </>
      )}
    </div>
  );
}

export function AccountPage() {
  const { user, refreshProfile } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verification, setVerification] = useState<{ message: string; code?: string } | null>(null);
  const [code, setCode] = useState('');

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setSuccess('Password changed. You have been signed out of your other devices.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendVerification = async (channel: 'email' | 'phone') => {
    setError(null);

    try {
      const status = await authApi.sendVerification(channel);
      setVerification({ message: status.message, code: status.developmentCode });
    } catch (err) {
      setError(toApiError(err));
    }
  };

  const confirmVerification = async (channel: 'email' | 'phone') => {
    setError(null);

    try {
      await authApi.confirmVerification(channel, code.trim());
      setSuccess('Verified.');
      setVerification(null);
      setCode('');
      await refreshProfile();
    } catch (err) {
      setError(toApiError(err));
    }
  };

  if (!user) return null;

  return (
    <div className="container" style={{ maxWidth: 640 }}>
      <h1 className="h3 mb-3">My account</h1>

      <InlineError message={error} />
      <InlineSuccess message={success} />

      <div className="sf-card sf-card-pad mb-3">
        <h2 className="sf-section-title">Details</h2>
        <dl className="row mb-0">
          <dt className="col-4 sf-muted fw-normal">Name</dt>
          <dd className="col-8">{user.fullName}</dd>

          <dt className="col-4 sf-muted fw-normal">Email</dt>
          <dd className="col-8">
            {user.email}{' '}
            {user.emailVerified ? (
              <span className="sf-pill sf-pill--ok">Verified</span>
            ) : (
              <button type="button" className="btn btn-sm btn-link p-0" onClick={() => sendVerification('email')}>
                Verify
              </button>
            )}
          </dd>

          <dt className="col-4 sf-muted fw-normal">Mobile</dt>
          <dd className="col-8">
            {user.phone ?? '—'}{' '}
            {user.phoneVerified ? (
              <span className="sf-pill sf-pill--ok">Verified</span>
            ) : (
              user.phone && (
                <button type="button" className="btn btn-sm btn-link p-0" onClick={() => sendVerification('phone')}>
                  Verify
                </button>
              )
            )}
          </dd>

          <dt className="col-4 sf-muted fw-normal">Member since</dt>
          <dd className="col-8">{dateTime(user.createdAtUtc)}</dd>
        </dl>

        {verification && (
          <div className="alert alert-info mt-3 mb-0">
            <p className="mb-2">{verification.message}</p>

            {verification.code && (
              <p className="mb-2 small">
                <strong>Development only.</strong> Code: <code>{verification.code}</code>
              </p>
            )}

            <div className="d-flex gap-2">
              <input
                className="form-control form-control-sm"
                style={{ maxWidth: 140 }}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                aria-label="Verification code"
              />
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => confirmVerification(user.emailVerified ? 'phone' : 'email')}
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="sf-card sf-card-pad">
        <h2 className="sf-section-title">Change password</h2>

        <form onSubmit={changePassword} noValidate>
          <Field label="Current password" htmlFor="current-password" required>
            <input
              id="current-password"
              type="password"
              className="form-control"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>

          <Field label="New password" htmlFor="new-password" required>
            <input
              id="new-password"
              type="password"
              className="form-control"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </Field>

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function PricingPage() {
  const [plans, setPlans] = useState<ListingPlan[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      setPlans(await billingApi.plans());
    } catch (err) {
      setError(toApiError(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = (plans ?? []).reduce<Record<string, ListingPlan[]>>((groups, plan) => {
    const key = plan.category ? categoryLabel(plan.category) : 'Add-ons';
    (groups[key] ??= []).push(plan);
    return groups;
  }, {});

  return (
    <div className="container">
      <h1 className="h3 mb-1">Pricing for owners</h1>
      <p className="sf-muted mb-4">
        PG and hotel branches are billed per branch, every month. Homes are billed once per listing. All prices
        include GST where it applies.
      </p>

      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !plans && <TableSkeleton rows={4} columns={3} />}

      {plans &&
        Object.entries(grouped).map(([group, items]) => (
          <section className="mb-4" key={group}>
            <h2 className="h5 mb-3">{group}</h2>
            <div className="row g-3">
              {items.map((plan) => (
                <div className="col-12 col-md-6 col-lg-4" key={plan.id}>
                  <div className="sf-card sf-card-pad h-100 d-flex flex-column">
                    <h3 className="h6">{plan.name}</h3>

                    <div className="h4 mb-0">{money(plan.totalAmount)}</div>
                    <p className="sf-muted small mb-2">
                      {money(plan.amount)} + {plan.taxPercentage}% tax ·{' '}
                      {plan.billingType === 'PerListing'
                        ? `${plan.durationDays} days per listing`
                        : `${plan.durationDays} days per branch`}
                    </p>

                    {plan.discountAmount > 0 && (
                      <p className="sf-pill sf-pill--ok align-self-start mb-2">
                        {money(plan.discountAmount)} off right now
                      </p>
                    )}

                    <ul className="small ps-3 mb-0">
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

      <div className="sf-card sf-card-pad">
        <h2 className="h6">How billing works</h2>
        <ul className="mb-0 small">
          <li>Each PG or hotel branch carries its own subscription. If one lapses, your other branches stay live.</li>
          <li>Each home listing is paid for on its own. Paying for one never publishes another.</li>
          <li>A listing goes live only after the payment is confirmed by the payment provider and the listing is reviewed.</li>
          <li>Changing a plan price never changes what an active subscription is already paying.</li>
        </ul>
      </div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="container">
      <EmptyState
        icon={<IconCompass />}
        title="Page not found"
        description="The page you were looking for does not exist, or it has moved."
        action={
          <Link to="/" className="btn btn-primary">
            Go to the home page
          </Link>
        }
      />
    </div>
  );
}
